import * as bootstrap from 'bootstrap';
import { EnableAddressAutocomplete } from 'smg/addressautocomplete';
import axios from 'smg/axios';
import * as countries from 'smg/countries';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as html from 'smg/html';
import * as models from 'smg/customer-api/models';
import * as phone from 'smg/phone';
import * as template from 'smg/template';
import * as toast from 'smg/toast';

import * as shippingoptions from '../shippingoptions';
import { NewShippingOption } from 'smg/customer-api/shippingoptions';

import { State } from './state';

// SetupShipping wires the "Shipping" accordion: the list of saved addresses,
// the new/edit address form, deletion with undo, and the collapse
// listeners that advance the flow to tax or payment after a selection.
export function SetupShipping(state: State): void {
    state.initShipping = () => initShipping(state);
    state.updateShippingSummary = () => updateShippingSummary(state);
    state.promptShippingOption = so => promptShippingOption(state, so);

    bindShippingCollapse(state);
    bindShippingRadioClick(state);
}

async function initShipping(state: State): Promise<void> {
    const { dom } = state;

    // Guest bootstrap runs initShipping more than once (main's explicit call,
    // plus promptShippingOption's own toggle click re-entering through
    // bindShippingCollapse). Each re-run re-renders the new-address form,
    // wiping anything already typed into it — and resetting the country/state
    // selects mid-entry. Guests have no saved list to refresh, so when their
    // form is already on screen, leave it alone (#8198).
    if (!state.sess?.user.id && dom.$shippingOptionsParent?.querySelector('#new-shipping-option-full-name')) {
        return;
    }

    state.startLoading();

    if (state.sess?.user.id) {
        await shippingoptions.Get({
            'data': 'address,created,lastOrdered,label,default',
            'sort': 'default desc,lastOrdered desc,created desc',
            'q': 'has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip',
        }).then(({ shippingOptions }) => {
            // Skip saved options whose stored phone fails the server tel validator, which would re-trap the customer at checkout.
            const validShippingOptions = shippingOptions.filter(so => phone.IsValid(so.address?.phoneNumber?.phone ?? ''));
            if (validShippingOptions.length) {
                state.userShippingOptions.clear();
                validShippingOptions.forEach(so => {
                    if (so.id) {
                        state.userShippingOptions.set(so.id, so);
                    }
                });
            }
        }).catch(ShowError);
    }

    dom.$shippingOptionsParent?.replaceChildren();
    dom.$shippingBody?.querySelector<HTMLElement>('#shipping-option-see-more-parent')?.remove();

    if (state.userShippingOptions.size) {
        await drawShippingList(state);
    } else {
        promptShippingOption(state, state.shippingOption);
    }

    state.stopLoading();
}

async function drawShippingList(state: State): Promise<void> {
    const { dom } = state;
    let shownCount = 0;

    const showMore = async () => {
        const sorted = Array.from(state.userShippingOptions.values()).sort((a, b) => {
            const aMs = lastOrderedMillis(a.lastOrdered);
            const bMs = lastOrderedMillis(b.lastOrdered);
            if (aMs !== undefined && bMs !== undefined) {
                return bMs - aMs;
            }
            return 0;
        });
        const targetCount = shownCount + 5;
        for (let i = shownCount; i < sorted.length && i < targetCount; i++) {
            const so = sorted[i];
            await shippingoptions.
                Draw(so, { el: template.Clone('#shipping-option-template').firstElementChild as HTMLElement, radio: true }).
                then(drawEl => {
                    if (i === 0) {
                        drawEl.querySelector<HTMLElement>('.tag-container')?.classList.remove('d-none');
                    }
                    const $radio = drawEl.querySelector<HTMLInputElement>('input[type="radio"]');
                    if ($radio && so.id && so.id === state.shippingOption?.id) {
                        $radio.checked = true;
                    }
                    dom.$shippingOptionsParent?.appendChild(drawEl);
                }).catch(ShowError);

            shownCount++;
            if (shownCount >= sorted.length) {
                dom.$shippingBody?.querySelector<HTMLButtonElement>('#shipping-option-see-more-button')?.remove();
            }
        }
        // Remove-then-add keeps us from double-binding edit/delete listeners.
        dom.$shippingOptionsParent?.querySelectorAll<HTMLElement>('.shipping-option').forEach($so => {
            const $edit = $so.querySelector<HTMLButtonElement>('.shipping-option-edit');
            $edit?.removeEventListener('click', editShippingOption);
            $edit?.addEventListener('click', editShippingOption);
            const $delete = $so.querySelector<HTMLButtonElement>('.shipping-option-delete');
            $delete?.removeEventListener('click', deleteShippingOption);
            $delete?.addEventListener('click', deleteShippingOption);
        });
    };

    if (dom.$shippingButtonsParent) {
        dom.$shippingBody?.insertBefore(template.Clone('#shipping-option-see-more'), dom.$internationalShippingWarning);
        dom.$shippingBody?.querySelector<HTMLButtonElement>('#shipping-option-see-more-button')?.addEventListener('click', showMore);
    }
    await showMore();

    dom.$shippingButtonsParent?.replaceChildren(template.Clone('#shipping-option-buttons'));
    dom.$shippingButtonsParent?.querySelector<HTMLButtonElement>('.shipping-option-new')?.
        addEventListener('click', () => promptShippingOption(state));
    bindContinueButton(state);

    function editShippingOption(e: Event) {
        if (e.target instanceof HTMLButtonElement && e.target.classList.contains('shipping-option-edit')) {
            const $so = e.target.closest('.shipping-option') as HTMLElement;
            const id = $so.querySelector<HTMLInputElement>('input[type="radio"]')?.value || '';
            const so = state.userShippingOptions.get(id);
            if (so) {
                promptShippingOption(state, so);
            }
        }
    }

    const bootstrapTooltips: bootstrap.Tooltip[] = [];
    function deleteShippingOption(e: Event) {
        if (!(e.target instanceof HTMLButtonElement) || !e.target.classList.contains('shipping-option-delete')) {
            return;
        }
        const $so = e.target.closest('.shipping-option') as HTMLElement;
        const id = $so.querySelector<HTMLInputElement>('input[type="radio"]')?.value || '';

        bootstrapTooltips.forEach(t => t.dispose());
        bootstrapTooltips.length = 0;

        const deleteTooltip = new bootstrap.Tooltip($so, {
            trigger: 'manual',
            placement: 'top',
            title: 'Are you sure you want to delete this address?',
            html: true,
            offset: [0, 16],
        });
        bootstrapTooltips.push(deleteTooltip);

        const $deleteConfirm = template.Clone(document.querySelector('.delete-confirm-template') as HTMLTemplateElement).firstElementChild as HTMLElement;
        const $confirm = $deleteConfirm.querySelector('.delete-confirm-button') as HTMLButtonElement;
        $confirm.addEventListener('click', async () => {
            await axios.delete(`/shipping-options/${id}`).then(() => {
                toast.Undo('Deleted shipping address.', async () => {
                    await axios.patch(`/shipping-options/${id}`, { 'active': true }).then(() => initShipping(state));
                });
                void initShipping(state);
                deleteTooltip.dispose();
            });
        });

        const $cancel = $deleteConfirm.querySelector('.delete-cancel-button') as HTMLButtonElement;
        $cancel.addEventListener('click', () => {
            deleteTooltip.dispose();
            bootstrapTooltips.length = 0;
        });

        deleteTooltip.setContent({ '.tooltip-inner': $deleteConfirm });
        deleteTooltip.show();
    }
}

function bindContinueButton(state: State): void {
    const { dom } = state;
    const $continueBtn = dom.$shippingButtonsParent?.querySelector<HTMLButtonElement>('.shipping-option-continue');
    if (!$continueBtn) {
        return;
    }
    $continueBtn.addEventListener('click', async () => {
        // Resolve the selection up front, before disabling anything. With no
        // radio checked (the list renders nothing pre-selected) or a selection
        // that doesn't map to a saved option, there's nothing to continue with
        // — keep the form interactive and prompt instead of disabling the list
        // and stalling forever (issue #5737).
        const selected = dom.$shippingOptionsParent?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
        const so = selected ? state.userShippingOptions.get(selected.value) : undefined;
        if (!so) {
            toast.Error('Please select a shipping address.');
            return;
        }

        dom.$shippingButtonsParent?.querySelectorAll('button').forEach($btn => {
            $btn.disabled = true;
        });
        if (dom.$shippingOptionsParent) {
            dom.$shippingOptionsParent.disabled = true;
        }

        try {
            state.shippingOption = so;
            state.customer = await state.getUser();
            await state.updateOrderSummary();
            state.updateShippingSummary();
            const shippingAddress = so.address;
            if (!shippingAddress) {
                return;
            }
            if (shippingAddress.countryID === models.CountryID.COUNTRIES_UNITED_STATES) {
                dom.$taxBodyToggle?.click();
            } else {
                dom.$paymentBodyToggle?.click();
            }
        } catch (err) {
            ShowError(err);
        } finally {
            // Always restore interactivity — no path may leave the list or
            // buttons permanently disabled.
            if (dom.$shippingOptionsParent) {
                dom.$shippingOptionsParent.disabled = false;
            }
            dom.$shippingButtonsParent?.querySelectorAll('button').forEach($btn => {
                $btn.disabled = false;
            });
        }
    });
}

function updateShippingSummary(state: State): void {
    const { dom } = state;
    const so = state.shippingOption;
    if (!so) {
        dom.$shippingPreviewAddress.innerHTML = 'Address';
        dom.$shippingPreviewPhone.textContent = 'Telephone';
        dom.$shippingPreviewEmail.textContent = 'Email';
        dom.$intlDutiesNotice.classList.add('d-none');
        return;
    }

    const shippingAddress = so.address;
    if (!shippingAddress) {
        dom.$shippingPreviewAddress.innerHTML = '';
        dom.$shippingPreviewPhone.textContent = '';
        dom.$shippingPreviewEmail.textContent = '';
        return;
    }
    dom.$shippingPreviewAddress.innerHTML = html.Nl2br(html.Escape(shippingAddress.toStringWithoutContact()));
    dom.$shippingPreviewPhone.textContent = shippingAddress.phoneNumber?.phone || '';
    dom.$shippingPreviewEmail.textContent = shippingAddress.emailAddress?.email || '';

    const payNum = dom.$sectionPayment?.querySelector<HTMLDivElement>('.number-circle');
    const addlNum = dom.$sectionAdditionalDetails?.querySelector<HTMLDivElement>('.number-circle');
    if (shippingAddress.countryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
        dom.$internationalShippingWarning.classList.remove('d-none');
        dom.$intlDutiesNotice.classList.remove('d-none');
        dom.$sectionTax?.classList.add('d-none');
        if (payNum) {
            payNum.textContent = '2';
        }
        if (addlNum) {
            addlNum.textContent = '3';
        }
    } else {
        dom.$internationalShippingWarning.classList.add('d-none');
        dom.$intlDutiesNotice.classList.add('d-none');
        dom.$sectionTax?.classList.remove('d-none');
        if (payNum) {
            payNum.textContent = '3';
        }
        if (addlNum) {
            addlNum.textContent = '4';
        }
    }
}

function promptShippingOption(state: State, shippingOptionInit?: models.IShippingOption) {
    const so = shippingOptionInit ? new models.ShippingOption(shippingOptionInit) : undefined;
    const shippingAddress = so?.address;
    const { dom } = state;
    if (!dom.$shippingBody?.classList.contains('show')) {
        dom.$shippingBodyToggle?.click();
    }
    const form = template.Clone('#new-shipping-option-form');
    if (!dom.$shippingOptionsParent) {
        return;
    }
    dom.$shippingOptionsParent.replaceChildren(form);
    dom.$shippingOptionsParent.classList.add('show');
    dom.$shippingButtonsParent?.replaceChildren();
    dom.$shippingBody?.querySelector<HTMLElement>('#shipping-option-see-more-parent')?.remove();

    const fields = queryShippingFormFields(dom.$shippingOptionsParent);
    const phoneIti = new phone.Phone(fields.$phone);

    countries.Selects(fields.$country, fields.$state, {
        defaultUnitedStates: true,
        stateParentSelector: 'div',
        phone: phoneIti,
        onStateHide: () => fields.$zipCode.parentElement?.classList.replace('col-4', 'col-6'),
        onStateShow: () => fields.$zipCode.parentElement?.classList.replace('col-6', 'col-4'),
    }).then(() => {
        if (shippingAddress?.countryID && shippingAddress.countryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
            fields.$country.value = String(shippingAddress.countryID);
            fields.$country.dispatchEvent(new Event('change'));
        }
    }).then(() => {
        if (shippingAddress?.stateID) {
            fields.$state.value = shippingAddress.stateID.toString();
        }
    }).catch(ShowError);

    EnableAddressAutocomplete(fields.$addressLine1, {
        city: fields.$city,
        state: fields.$state,
        zip: fields.$zipCode,
        country: fields.$country,
    });

    if (so && shippingAddress) {
        fields.$fullName.value = shippingAddress.fullName || '';
        fields.$company.value = shippingAddress.organization || '';
        fields.$addressLine1.value = shippingAddress.addressLine1 || '';
        fields.$addressLine2.value = shippingAddress.addressLine2 || '';
        fields.$city.value = shippingAddress.city || '';
        fields.$zipCode.value = shippingAddress.postalCode || '';
        fields.$email.value = shippingAddress.emailAddress?.email || '';
        void phoneIti.iti.promise.then(() => phoneIti.iti.setNumber(shippingAddress.phoneNumber?.phone ?? ''));

        const $confirm = dom.$shippingOptionsParent.querySelector<HTMLButtonElement>('#new-shipping-option-confirm') as HTMLButtonElement;
        $confirm.innerText = 'Save';
    }

    if (state.userShippingOptions.size) {
        const $cancel = dom.$shippingOptionsParent.querySelector<HTMLButtonElement>('#new-shipping-option-cancel') as HTMLButtonElement;
        $cancel.classList.remove('d-none');
        $cancel.addEventListener('click', () => {
            void initShipping(state);
        });
    }

    bindShippingFormSubmit(state, fields, phoneIti, so);
}

interface shippingFormFields {
    $fullName: HTMLInputElement;
    $company: HTMLInputElement;
    $country: HTMLSelectElement;
    $addressLine1: HTMLInputElement;
    $addressLine2: HTMLInputElement;
    $city: HTMLInputElement;
    $state: HTMLSelectElement;
    $zipCode: HTMLInputElement;
    $email: HTMLInputElement;
    $phone: HTMLInputElement;
}

function queryShippingFormFields($parent: HTMLElement): shippingFormFields {
    return {
        $fullName: $parent.querySelector<HTMLInputElement>('#new-shipping-option-full-name') as HTMLInputElement,
        $company: $parent.querySelector<HTMLInputElement>('#new-shipping-option-company') as HTMLInputElement,
        $country: $parent.querySelector<HTMLSelectElement>('#new-shipping-option-country') as HTMLSelectElement,
        $addressLine1: $parent.querySelector<HTMLInputElement>('#new-shipping-option-address-line-1') as HTMLInputElement,
        $addressLine2: $parent.querySelector<HTMLInputElement>('#new-shipping-option-address-line-2') as HTMLInputElement,
        $city: $parent.querySelector<HTMLInputElement>('#new-shipping-option-city') as HTMLInputElement,
        $state: $parent.querySelector<HTMLSelectElement>('#new-shipping-option-state') as HTMLSelectElement,
        $zipCode: $parent.querySelector<HTMLInputElement>('#new-shipping-option-zip') as HTMLInputElement,
        $email: $parent.querySelector<HTMLInputElement>('#new-shipping-option-email') as HTMLInputElement,
        $phone: $parent.querySelector<HTMLInputElement>('#new-shipping-option-phone') as HTMLInputElement,
    };
}

function bindShippingFormSubmit(
    state: State,
    fields: shippingFormFields,
    phoneIti: phone.Phone,
    existingInit?: models.IShippingOption,
): void {
    const existing = existingInit ? new models.ShippingOption(existingInit) : undefined;
    const { dom } = state;
    const $form = dom.$shippingOptionsParent?.querySelector<HTMLFormElement>('form') as HTMLFormElement;
    const $fieldset = $form.querySelector<HTMLFieldSetElement>('fieldset') as HTMLFieldSetElement;
    $form.noValidate = true;
    let submitting = false;

    $form.addEventListener('submit', async e => {
        e.preventDefault();
        if (submitting) {
            return;
        }
        submitting = true;
        let loadingStarted = false;
        const loadingTimeout = window.setTimeout(() => {
            loadingStarted = true;
            state.startLoading();
        }, 150);

        try {
            await phoneIti.validate();
            if (!ReportValidity($form)) {
                return;
            }
            $fieldset.disabled = true;

            let country;
            let stateCountry;
            if (fields.$country) {
                country = await countries.GetCountry(parseInt(fields.$country.value, 10)).then(c => c?.toJSON());
                if (fields.$state) {
                    stateCountry = await countries.GetState(parseInt(fields.$country.value, 10), parseInt(fields.$state.value, 10)).then(s => s?.toJSON());
                }
            }

            const newShippingOption = new models.ShippingOption({
                userID: state.sess?.user.id,
                address: {
                    fullName: fields.$fullName?.value,
                    organization: fields.$company?.value,
                    addressLine1: fields.$addressLine1?.value,
                    addressLine2: fields.$addressLine2?.value,
                    city: fields.$city?.value,
                    postalCode: fields.$zipCode?.value,
                    phoneNumber: { phone: await phoneIti.format() },
                    emailAddress: { email: fields.$email?.value },
                    country,
                    state: stateCountry,
                },
            });
            state.shippingOption = newShippingOption;
            state.customer = await state.getUser();
            if (existing?.id) {
                void axios.delete(`/shipping-options/${existing.id}`);
            }

            const resp = await NewShippingOption(state.customer, newShippingOption);
            state.shippingOption = resp;
            await state.updateOrderSummary();
            state.updateShippingSummary();
            if (!state.sess?.user.id) {
                // NewShippingOption just created the guest's user account.
                state.customer = await state.getUser();
            }
            // Logged-in and guest advance identically (#8458); reopening the
            // shipping section re-runs initShipping via bindShippingCollapse,
            // so the saved list picks up the new address without a redraw here.
            const shippingAddress = resp.address;
            if (shippingAddress?.countryID === models.CountryID.COUNTRIES_UNITED_STATES) {
                dom.$taxBodyToggle?.click();
            } else {
                dom.$paymentBodyToggle?.click();
            }
        } catch (err) {
            ShowError(err);
            state.shippingOption = undefined;
        } finally {
            window.clearTimeout(loadingTimeout);
            submitting = false;
            $fieldset.disabled = false;
            if (loadingStarted) {
                state.stopLoading();
            }
        }
    });
}

function bindShippingCollapse(state: State): void {
    const { dom } = state;
    dom.$shippingBody?.addEventListener('shown.bs.collapse', () => {
        dom.$spinner.classList.add('d-none');
        dom.$sectionShipping?.classList.remove('d-none');
        dom.$sectionTax?.classList.remove('d-none');
        dom.$sectionPayment?.classList.remove('d-none');
        dom.$sectionAdditionalDetails?.classList.remove('d-none');
        dom.$orderSummaryColumn?.classList.remove('d-none');
    });

    dom.$shippingBodyToggle?.addEventListener('click', () => {
        // setTimeout lets Bootstrap update .collapsed before we read it.
        setTimeout(() => {
            if (!dom.$shippingBodyToggle?.classList.contains('collapsed')) {
                void initShipping(state);
                setTimeout(() => {
                    dom.$shippingBody?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
            }
        }, 50);
    });
}

function bindShippingRadioClick(state: State): void {
    state.dom.$shippingBody?.addEventListener('click', e => {
        if (!(e.target instanceof HTMLInputElement) || !e.target.classList.contains('shipping-option-input')) {
            return;
        }
        const so = state.userShippingOptions.get(e.target.value);
        if (so) {
            state.shippingOption = so;
            void state.updateOrderSummary();
            state.updateShippingSummary();
        }
    });
}

// lastOrdered may be a luxon DateTime (from HyejooShippingOption) or a legacy
// ISO string — normalize both to epoch millis for client-side sorting.
function lastOrderedMillis(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    if (typeof value === 'object' && 'isValid' in value && 'valueOf' in value) {
        const dt = value as { isValid: boolean; valueOf: () => number };
        return dt.isValid ? dt.valueOf() : undefined;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const ms = new Date(value).valueOf();
        return Number.isNaN(ms) ? undefined : ms;
    }
    return undefined;
}
